/**
 * The aids beside one item on the player (practice and test): the button,
 * `Alt+P` and `Esc`, the table under the item's buttons, and the events
 * (`aid.ts`). One opening belongs to one item: the item leaving her screen
 * closes it, and so does „Baigti".
 */
import { useCallback, useEffect, useRef, useState } from "react";

import type { ChildItem } from "../api";
import { useChild } from "../ctx";
import type { KeyLike } from "../surfaces/keys";
import type { Recorder } from "../trace";
import { type AidCloseBy, type AidOpen, type AidVia, PERIODIC, aidEntry, aidKey, closedBody, openedBody } from "./aid";
import { PeriodicTable } from "./PeriodicTable";

export interface AidHandle {
  offered: boolean;
  open: boolean;
  toggle(via: AidVia, k: KeyLike | null): void;
  /** Idempotent: an aid already closed writes nothing. */
  close(by: AidCloseBy, k?: KeyLike | null): void;
}

function keyLike(e: KeyboardEvent): KeyLike {
  return { key: e.key, code: e.code, shiftKey: e.shiftKey, ctrlKey: e.ctrlKey, metaKey: e.metaKey, altKey: e.altKey, repeat: e.repeat };
}

export function useAid(item: ChildItem, recorder: Recorder): AidHandle {
  const ctx = useChild();
  const offered = (item.aids ?? []).includes(PERIODIC); // an API a deploy behind sends none
  const [open, setOpen] = useState(false);
  const cur = useRef<AidOpen | null>(null);
  // the latest of each, for the key listener and the unmount, which outlive a render
  const live = useRef({ ctx, recorder, handle: item.handle });
  live.current = { ctx, recorder, handle: item.handle };

  const close = useCallback((by: AidCloseBy, k: KeyLike | null = null) => {
    const o = cur.current;
    if (!o) return;
    cur.current = null;
    setOpen(false);
    const { ctx: c, recorder: r } = live.current;
    if (by === "key" || by === "click") r.push(aidEntry(PERIODIC, "close", k), false);
    c.emit("aid.closed", closedBody(o, by, performance.now()));
  }, []);

  const toggle = useCallback(
    (via: AidVia, k: KeyLike | null) => {
      if (cur.current) {
        close(via, k);
        return;
      }
      const { ctx: c, recorder: r, handle } = live.current;
      const id = c.emit("aid.opened", openedBody(PERIODIC, handle, via));
      cur.current = { id, at: performance.now() };
      setOpen(true);
      r.push(aidEntry(PERIODIC, "open", k), false);
    },
    [close],
  );

  // Capture phase, before any answer surface reads the key; they skip a key already taken.
  useEffect(() => {
    if (!offered) return;
    const h = (e: KeyboardEvent) => {
      const k = keyLike(e);
      const a = aidKey(k, cur.current !== null);
      if (!a) return;
      e.preventDefault();
      if (a === "toggle") toggle("key", k);
      else close("key", k);
    };
    window.addEventListener("keydown", h, true);
    return () => window.removeEventListener("keydown", h, true);
  }, [offered, toggle, close]);

  useEffect(() => () => close("item_end"), [close]);

  return { offered, open, toggle, close };
}

/** The button and, when open, the table: under the item's buttons, never over her answer. */
export function Aids({ aid }: { aid: AidHandle }) {
  if (!aid.offered) return null;
  return (
    <div className="aids">
      <button
        type="button"
        className="quiet-link"
        aria-expanded={aid.open}
        data-act="periodic"
        onMouseDown={(e) => e.preventDefault()} // the focus stays in her answer
        onClick={() => aid.toggle("click", null)}
      >
        Periodinė lentelė <span className="key-hint">Alt+P</span>
      </button>
      {aid.open && <PeriodicTable inline close={() => aid.close("click")} />}
    </div>
  );
}
